import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { IconShieldCheck, IconUserCheck } from '@tabler/icons-react';
import { useCallback, useId, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heading, Text } from '../components/atomic';
import SEO from '../components/SEO';
import SecureEmail from '../components/SecureEmail';
import { Logo } from '../components/ui';

/**
 * @typedef {Object} FormState
 * @property {string} email - User email address
 * @property {string} password - User password
 * @property {boolean} rememberMe - Remember me checkbox state
 */

/**
 * @typedef {Object} FormErrors
 * @property {string} [email] - Email field error message
 * @property {string} [password] - Password field error message
 */

/**
 * Validates email format
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid email format
 */
function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Professional Sign In page component for EllaFin
 *
 * Features a split-layout design with brand panel on left and login form on right.
 * Includes client-side validation, password visibility toggle, remember me option,
 * and appropriate loading/error states. Currently shows "Early Access" messaging
 * as the customer portal is coming soon.
 *
 * @returns {JSX.Element} Sign In page component
 */
function SignIn() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const formId = useId();
  const emailId = `${formId}-email`;
  const passwordId = `${formId}-password`;

  /** @type {[FormState, Function]} */
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });

  /** @type {[FormErrors, Function]} */
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({ email: false, password: false });

  /**
   * Validate a single field
   * @param {string} name - Field name
   * @param {string} value - Field value
   * @returns {string} Error message or empty string
   */
  const validateField = useCallback((name, value) => {
    switch (name) {
      case 'email':
        if (!value.trim()) {
          return 'Email address is required';
        }
        if (!isValidEmail(value)) {
          return 'Please enter a valid email address';
        }
        return '';
      case 'password':
        if (!value) {
          return 'Password is required';
        }
        if (value.length < 6) {
          return 'Password must be at least 6 characters';
        }
        return '';
      default:
        return '';
    }
  }, []);

  /**
   * Handle form input changes
   * @param {React.ChangeEvent<HTMLInputElement>} event - Input change event
   */
  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Clear submit error when user starts typing
    if (submitError) {
      setSubmitError('');
    }

    // Validate on change if field has been touched
    if (touched[name] && type !== 'checkbox') {
      const error = validateField(name, value);
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  /**
   * Handle field blur for validation
   * @param {React.FocusEvent<HTMLInputElement>} event - Blur event
   */
  const handleBlur = (event) => {
    const { name, value } = event.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  /**
   * Toggle password visibility
   */
  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  /**
   * Validate all form fields
   * @returns {boolean} True if form is valid
   */
  const validateForm = () => {
    const emailError = validateField('email', formData.email);
    const passwordError = validateField('password', formData.password);

    setErrors({
      email: emailError,
      password: passwordError,
    });

    setTouched({ email: true, password: true });

    return !emailError && !passwordError;
  };

  /**
   * Handle form submission
   * Shows error message as platform is in Early Access
   * @param {React.FormEvent} event - Form submit event
   */
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    // Simulate API call delay
    await new Promise((resolve) => setTimeout(resolve, 1500));

    setLoading(false);
    setSubmitError(
      'The customer portal is currently in Early Access. Please contact us for account access or to join the waitlist.'
    );

    // Track login attempt (placeholder for analytics)
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'login_attempt', {
        event_category: 'authentication',
        event_label: 'early_access',
      });
    }
  };

  /**
   * Handle logo click - navigate to homepage
   */
  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      <SEO
        title="Sign In to EllaFin"
        description="Sign in to your EllaFin account. The customer portal is currently in Early Access. Request access or join the waitlist to get started."
        path="/sign-in"
        noIndex={true}
        keywords="login, sign in, dashboard access, FinOps dashboard"
      />

      {/* Brand Panel - Left Side (bold hero style) */}
      <Box
        sx={{
          width: { xs: '100%', md: '45%' },
          minHeight: { xs: '340px', md: '100vh' },
          backgroundColor: theme.custom?.footerBackground || '#13152b',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: { xs: 4, md: 6 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background image with overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/AdobeStock_1890471615.jpeg)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.15,
          }}
        />

        {/* Green accent bar at top */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            backgroundColor: theme.palette.secondary.main,
          }}
        />

        {/* Brand Content */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            textAlign: 'center',
            maxWidth: '420px',
          }}
        >
          <Logo
            onClick={handleLogoClick}
            sx={{
              height: { xs: '50px', md: '65px' },
              mb: 5,
              filter: 'brightness(0) invert(1)',
              cursor: 'pointer',
            }}
            alt="EllaFin™ Logo - Click to return to homepage"
          />

          <Heading
            variant="h1"
            sx={{
              color: '#ffffff',
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.75rem', lg: '3.25rem' },
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              mb: 2,
            }}
          >
            Welcome Back
          </Heading>

          <Text
            sx={{
              color: theme.palette.secondary.main,
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              fontStyle: 'italic',
              mb: 5,
              lineHeight: 1.6,
            }}
          >
            Finance-First <span style={{ fontWeight: 700 }}>Fin</span>Ops for
            modern teams
          </Text>

          {/* Trust Indicators */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              flexWrap: 'wrap',
              borderTop: `1px solid rgba(255, 255, 255, 0.1)`,
              pt: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.9rem',
              }}
            >
              <IconShieldCheck
                size={20}
                stroke={1.5}
                color={theme.palette.secondary.main}
              />
              <span>SOC 2 Compliant</span>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.9rem',
              }}
            >
              <IconUserCheck
                size={20}
                stroke={1.5}
                color={theme.palette.secondary.main}
              />
              <span>Enterprise Security</span>
            </Box>
          </Box>

          {/* AWS Marketplace Badge */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/images/aws-marketplace-badge.png`}
              alt="Available in AWS Marketplace"
              sx={{
                height: { xs: 60, md: 75 },
                filter: 'brightness(0) invert(1)',
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Form Panel - Right Side */}
      <Box
        sx={{
          width: { xs: '100%', md: '55%' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: { xs: 3, sm: 4, md: 6 },
          backgroundColor: '#ffffff',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '440px',
          }}
        >
          {/* Mobile Logo */}
          {isMobile && (
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Logo
                onClick={handleLogoClick}
                sx={{ height: '40px' }}
                alt="EllaFin™ Logo - Click to return to homepage"
              />
            </Box>
          )}

          {/* Logo + subtitle */}
          <Box sx={{ mb: 4 }}>
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/logos/ellafin-logo.png`}
              alt="EllaFin™"
              onClick={handleLogoClick}
              sx={{
                height: { xs: 36, md: 44 },
                width: 'auto',
                cursor: 'pointer',
                display: 'block',
                mb: 1.5,
              }}
            />
            <Text
              sx={{
                color: '#6c757d',
                fontSize: '0.9rem',
                mb: 0,
              }}
            >
              Sign in to access your dashboard
            </Text>
          </Box>

          {/* Login Form */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              position: 'relative',
            }}
          >
            {/* Error Alert */}
            {submitError && (
              <Alert
                severity="info"
                sx={{
                  mb: 3,
                  '& .MuiAlert-message': {
                    width: '100%',
                  },
                }}
              >
                <Box>
                  {submitError}
                  <Box sx={{ mt: 1 }}>
                    <SecureEmail
                      emailKey="support"
                      component="span"
                      sx={{
                        color: 'inherit',
                        fontWeight: 600,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                    >
                      Contact Support
                    </SecureEmail>
                    {' or '}
                    <MuiLink
                      component={Link}
                      to="/early-access"
                      sx={{
                        color: 'inherit',
                        fontWeight: 600,
                        textDecoration: 'underline',
                      }}
                    >
                      Join Early Access
                    </MuiLink>
                  </Box>
                </Box>
              </Alert>
            )}

            {/* Email Field */}
            <TextField
              id={emailId}
              name="email"
              type="email"
              label="Email Address"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email && Boolean(errors.email)}
              helperText={touched.email && errors.email}
              fullWidth
              required
              disabled={loading}
              autoComplete="email"
              slotProps={{
                htmlInput: {
                  'aria-label': 'Email address',
                  'aria-required': 'true',
                  'aria-invalid': touched.email && Boolean(errors.email),
                },
              }}
              sx={{
                mb: 2.5,
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: theme.palette.grey[500],
                  '&.Mui-focused': {
                    color: theme.palette.primary.main,
                  },
                },
              }}
            />

            {/* Password Field */}
            <TextField
              id={passwordId}
              name="password"
              type={showPassword ? 'text' : 'password'}
              label="Password"
              value={formData.password}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.password && Boolean(errors.password)}
              helperText={touched.password && errors.password}
              fullWidth
              required
              disabled={loading}
              autoComplete="current-password"
              slotProps={{
                htmlInput: {
                  'aria-label': 'Password',
                  'aria-required': 'true',
                  'aria-invalid': touched.password && Boolean(errors.password),
                },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={
                          showPassword ? 'Hide password' : 'Show password'
                        }
                        onClick={handleTogglePassword}
                        edge="end"
                        disabled={loading}
                        sx={{
                          color: theme.palette.grey[500],
                          '&:hover': {
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: theme.palette.grey[500],
                  '&.Mui-focused': {
                    color: theme.palette.primary.main,
                  },
                },
              }}
            />

            {/* Remember Me & Forgot Password Row */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    disabled={loading}
                    sx={{
                      color: theme.palette.grey[500],
                      '&.Mui-checked': {
                        color: theme.palette.primary.main,
                      },
                    }}
                  />
                }
                label={
                  <Text
                    component="span"
                    sx={{
                      color: theme.palette.grey[500],
                      fontSize: '0.875rem',
                      mb: 0,
                    }}
                  >
                    Remember me
                  </Text>
                }
              />

              <MuiLink
                component={Link}
                to="/forgot-password"
                sx={{
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Forgot password?
              </MuiLink>
            </Box>

            {/* Sign In Button */}
            <Box
              component="button"
              type="submit"
              disabled={loading}
              sx={{
                width: '100%',
                backgroundColor: loading
                  ? theme.palette.grey[500]
                  : theme.palette.primary.main,
                color: 'white',
                py: 1.75,
                px: 3,
                fontSize: '1rem',
                fontWeight: 600,
                fontFamily: '"Manrope", sans-serif',
                border: 'none',
                borderRadius: '9999px',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                transition: 'all 0.2s ease',
                '&:hover:not(:disabled)': {
                  backgroundColor: theme.palette.primary.dark,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${theme.palette.primary.main}4D`,
                },
                '&:focus': {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: '2px',
                },
              }}
            >
              {loading
                ? <>
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                    <span>Signing In...</span>
                  </>
                : 'Sign In'}
            </Box>

            {/* Divider */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                my: 3,
              }}
            >
              <Box
                sx={{ flex: 1, height: '1px', backgroundColor: '#e8e8e8' }}
              />
              <Text
                sx={{
                  px: 2,
                  color: theme.palette.grey[500],
                  fontSize: '0.875rem',
                  mb: 0,
                }}
              >
                or
              </Text>
              <Box
                sx={{ flex: 1, height: '1px', backgroundColor: '#e8e8e8' }}
              />
            </Box>

            {/* SSO Button (Placeholder) */}
            <Tooltip title="SSO integration coming soon" arrow placement="top">
              <span>
                <Box
                  component="button"
                  type="button"
                  disabled
                  sx={{
                    width: '100%',
                    backgroundColor: 'transparent',
                    color: theme.palette.grey[500],
                    py: 1.5,
                    px: 3,
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    fontFamily: '"Manrope", sans-serif',
                    border: '1px solid #dee2e6',
                    borderRadius: '9999px',
                    cursor: 'not-allowed',
                    opacity: 0.6,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <IconShieldCheck size={20} stroke={1.5} />
                  Sign in with SSO
                  <Box
                    component="span"
                    sx={{
                      fontSize: '0.75rem',
                      backgroundColor: '#e9ecef',
                      px: 1,
                      py: 0.25,
                      borderRadius: '4px',
                      ml: 0.5,
                    }}
                  >
                    Coming Soon
                  </Box>
                </Box>
              </span>
            </Tooltip>
          </Box>

          {/* Alternative Actions */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Text
              sx={{
                color: theme.palette.grey[500],
                fontSize: '0.95rem',
                mb: 1.5,
                textAlign: 'center',
              }}
            >
              Don't have an account?
            </Text>
            <MuiLink
              component={Link}
              to="/early-access"
              sx={{
                color: theme.palette.primary.main,
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Request Early Access
            </MuiLink>
          </Box>

          {/* Footer Links */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              mt: 4,
              pt: 3,
              borderTop: '1px solid #e8e8e8',
              flexWrap: 'wrap',
            }}
          >
            <MuiLink
              component={Link}
              to="/privacy"
              sx={{
                color: theme.palette.grey[500],
                textDecoration: 'none',
                fontSize: '0.8rem',
                '&:hover': {
                  color: theme.palette.primary.main,
                },
              }}
            >
              Privacy Policy
            </MuiLink>
            <MuiLink
              component={Link}
              to="/terms"
              sx={{
                color: theme.palette.grey[500],
                textDecoration: 'none',
                fontSize: '0.8rem',
                '&:hover': {
                  color: theme.palette.primary.main,
                },
              }}
            >
              Terms of Service
            </MuiLink>
            <SecureEmail
              emailKey="support"
              component="span"
              sx={{
                color: theme.palette.grey[500],
                textDecoration: 'none',
                fontSize: '0.8rem',
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.primary.main,
                },
              }}
            >
              Contact Support
            </SecureEmail>
          </Box>

          {/* Copyright */}
          <Text
            sx={{
              textAlign: 'center',
              color: theme.palette.grey[400],
              fontSize: '0.75rem',
              mt: 2,
            }}
          >
            &copy; {new Date().getFullYear()} EllaFin™, Inc. All rights
            reserved.
          </Text>
        </Box>
      </Box>
    </Box>
  );
}

export default SignIn;
